<template>
  <main class="extensions-directory">
    <section class="panel">
      <div class="header">
        <div class="title-line"><span>Plugins</span><small>{{ filteredExtensions.length }}/{{ extensions.length }}</small></div>
        <div class="header-actions">
          <small class="active-hint">{{ activeExtension ? `Active: ${activeExtension.label}` : 'Console active' }}</small>
          <div class="filter-picker">
            <IconAction name="filter" :label="`Filter: ${ownershipFilterLabel}`" :active="ownershipFilter !== 'all'" :size="30" @click="filterMenuOpen = !filterMenuOpen" />
            <div v-if="filterMenuOpen" class="filter-menu" role="menu">
              <button v-for="tab in ownershipTabs" :key="tab.key" type="button" :class="{ active: ownershipFilter === tab.key }" @click="selectOwnershipFilter(tab.key)">{{ tab.label }}</button>
            </div>
          </div>
          <button v-if="canCreateExtensions" class="authoring-help" type="button" aria-label="Create with AI" title="Create with AI" @click="openAuthoringHelp">🪄</button>
        </div>
      </div>

      <div class="tabs" role="tablist" aria-label="Plugin categories">
        <button v-for="tab in categoryTabs" :key="tab.key" type="button" :class="{ active: activeCategory === tab.key }" @click="activeCategory = tab.key">{{ tab.label }}</button>
      </div>

      <label class="engine-filter">Engine
        <select v-model="selectedEngine" aria-label="Filter plugins by engine">
          <option value="">All engines</option>
          <option v-for="engine in engines" :key="engine.id" :value="engine.id">{{ engine.name }}</option>
        </select>
      </label>

      <div class="body">
        <ul class="extension-grid">
          <li v-for="extension in filteredExtensions" :key="extension.id" class="extension-card" :class="{ active: isRunning(extension) }" @click="selectExtension(extension)">
            <div class="extension-media">
              <img v-if="extension.iconUrl && !brokenIcons[extension.id]" :src="extension.iconUrl" :alt="`${extension.label} icon`" @error="brokenIcons[extension.id] = true">
              <span v-else>{{ fallbackIcon(extension) }}</span>
            </div>
            <div class="extension-main">
              <div class="extension-head"><strong>{{ extension.label }}</strong><span class="release-version">{{ releaseLabel(extension) }}</span></div>
              <p>{{ extension.description || 'No description.' }}</p>
              <div v-if="characterVariants(extension).length" class="character-strip" aria-label="Playable characters">
                <span
                  v-for="variant in characterVariants(extension)"
                  :key="variant.id"
                  class="character-portrait"
                  :title="variant.name"
                  :aria-label="variant.name"
                  role="img"
                >
                  <img :src="characterAssetURL(extension, variant)" :alt="variant.name" :style="characterImageStyle(variant)">
                </span>
                <span v-if="characterOverflow(extension)">+{{ characterOverflow(extension) }}</span>
              </div>
              <div class="card-status">
                <div class="extension-meta">
                  <span>{{ runtimeLabel(extension.runtime) }}</span>
                  <span>{{ categoryLabel(extension.category) }}</span>
                  <span v-if="extension.installation" class="state installed">{{ installedLabel(extension) }}</span>
                  <span v-else-if="extension.state === 'Review'" class="state review">In review</span>
                  <span v-if="draftLabel(extension)" class="authoring">{{ draftLabel(extension) }}</span>
                </div>
                <small class="developer">by @{{ extension.authorName }}</small>
                <IconAction class="card-primary-action" :name="primaryActionIcon(extension)" :label="primaryActionLabel(extension)" :text="primaryActionText(extension)" :active="isRunning(extension)" :size="28" :disabled="busy" @click.stop="runPrimaryAction(extension)" />
              </div>
              <div class="card-footer">
                <div class="tags"><span class="engine-badge" :title="`Engine: ${engineLabel(extension.engine)}`">{{ engineLabel(extension.engine) }}</span><span v-for="tag in extension.tags.slice(0, 3)" :key="tag">{{ tag }}</span></div>
                <span class="rating" aria-label="Rating not available yet">☆ ☆ ☆ ☆ ☆</span>
              </div>
            </div>
          </li>
          <li v-if="filteredExtensions.length === 0" class="empty"><strong>No plugins</strong></li>
        </ul>
      </div>
    </section>

    <div v-if="selectedExtension" class="detail-overlay" @click.self="closeDetail">
      <aside class="detail-card" role="dialog" aria-modal="true" :aria-label="selectedExtension.label">
        <button class="detail-close" type="button" aria-label="Close plugin details" @click="closeDetail">×</button>
        <div class="detail-head">
          <div class="detail-title">
            <div class="detail-icon">
              <img v-if="selectedExtension.iconUrl && !brokenIcons[`detail:${selectedExtension.id}`]" :src="selectedExtension.iconUrl" :alt="`${selectedExtension.label} icon`" @error="brokenIcons[`detail:${selectedExtension.id}`] = true">
              <span v-else>{{ fallbackIcon(selectedExtension) }}</span>
            </div>
            <div><small>{{ categoryLabel(selectedExtension.category) }}</small><h2>{{ selectedExtension.label }}</h2><div class="detail-version"><strong>{{ detailVersionLabel(selectedExtension) }}</strong><span>by @{{ selectedExtension.authorName }}</span></div></div>
          </div>
          <span class="status-pill" :class="selectedExtension.state.toLowerCase()">{{ stateLabel(selectedExtension) }}</span>
        </div>

        <p class="description">{{ selectedExtension.description || 'No description.' }}</p>
        <dl class="facts">
          <div v-if="selectedExtension.latestPublishedVersion"><dt>Release</dt><dd>{{ releaseVersionLabel(selectedExtension) }}</dd></div>
          <div v-if="selectedExtension.author && selectedExtension.draftVersion"><dt>Draft</dt><dd>{{ authoringVersionLabel(selectedExtension) }}</dd></div>
          <div v-if="deployVersion"><dt>Deploy</dt><dd class="mono">{{ deployVersion }}</dd></div>
          <div><dt>Kind</dt><dd>{{ kindLabel(selectedExtension) }}</dd></div>
          <div><dt>Engine</dt><dd>
            <select v-if="selectedExtension.author" :value="selectedExtension.engine" :disabled="busy" aria-label="Extension engine" @change="updateEngine(selectedExtension, $event.target.value)">
              <option v-for="engine in engines" :key="engine.id" :value="engine.id">{{ engine.name }}{{ engine.integration_status === 'planned' ? ' (planned integration)' : '' }}</option>
            </select>
            <span v-else>{{ engineLabel(selectedExtension.engine) }}</span>
          </dd></div>
          <div><dt>Access</dt><dd>{{ accessLabel(selectedExtension) }}</dd></div>
          <div><dt>Runtime</dt><dd>{{ runtimeLabel(selectedExtension.runtime) }}</dd></div>
          <div><dt>Entrypoint</dt><dd class="mono">{{ selectedExtension.entrypoint }}</dd></div>
          <div><dt>SDK</dt><dd class="mono">{{ selectedExtension.sdkVersion }}</dd></div>
        </dl>
        <div class="detail-footer">
          <div v-if="selectedExtension.tags.length" class="detail-tags"><span v-for="tag in selectedExtension.tags" :key="tag">{{ tag }}</span></div>
          <span class="rating" aria-label="Rating not available yet">☆ ☆ ☆ ☆ ☆</span>
        </div>
        <div v-if="error" class="error">{{ error }}</div>
        <div class="detail-actions">
          <IconAction v-if="characterVariants(selectedExtension).length" name="characters" label="Heroes" text="Heroes" :size="34" @click="openCharacters(selectedExtension)" />
          <IconAction :name="primaryActionIcon(selectedExtension)" :label="primaryActionLabel(selectedExtension)" :text="primaryActionText(selectedExtension)" :active="isRunning(selectedExtension)" :size="34" :disabled="busy" @click="runPrimaryAction(selectedExtension)" />
          <IconAction v-if="selectedExtension.author && selectedExtension.draftVersion" name="reset" label="Reset draft" text="Reset draft" tone="danger" :size="34" :disabled="busy" @click="removeDraft(selectedExtension)" />
          <IconAction v-if="selectedExtension.author && !selectedExtension.draftVersion && selectedExtension.history.some(item => item.status === 'published')" name="edit" label="Edit" text="Edit" :size="34" :disabled="busy" @click="reopenAsDraft(selectedExtension)" />
          <IconAction v-if="selectedExtension.installation" name="close" label="Uninstall" text="Uninstall" tone="danger" :size="34" :disabled="busy" @click="removeInstallation(selectedExtension)" />
          <IconAction v-if="selectedExtension.author && selectedExtension.state === 'Draft' && !selectedExtension.history.some(item => item.status === 'published')" name="close" label="Delete Extension" text="Delete" tone="danger" :size="34" :disabled="busy" @click="removeExtension(selectedExtension)" />
        </div>
      </aside>
    </div>

    <div v-if="showAuthoringHelp" class="detail-overlay" @click.self="showAuthoringHelp = false">
      <aside class="detail-card authoring-card" role="dialog" aria-modal="true" aria-label="Create a Plugin with AI">
        <button class="detail-close" type="button" aria-label="Close" @click="showAuthoringHelp = false">×</button>
        <div class="wand-mark" aria-hidden="true">🪄</div>
        <div><small class="eyebrow">PREMIUM</small><h2>Create a Plugin with AI</h2></div>
        <p class="description">Describe the target engine as well as the game. The engine can be changed later in plugin details.</p>
        <p class="description">Connect an AI agent to AiMe, authorize it with your Phantom account, then describe the Plugin you want.</p>
        <ol class="wizard-steps">
          <li><strong>Connect</strong><span>Add the AiMe MCP server to your AI client.</span></li>
          <li><strong>Authorize</strong><span>Sign in with the same Phantom owner you use here.</span></li>
          <li><strong>Describe</strong><span>Ask: “Create a new game…” and explain the concept and visual style.</span></li>
          <li><strong>Build</strong><span>The agent reuses platform capabilities, creates one mutable plugin draft and previews it without deploying core.</span></li>
          <li><strong>Publish</strong><span>Review the draft, publish an immutable version, then install it in Play.</span></li>
        </ol>
        <p class="authoring-note">You stay in control: mutations require confirmation, and only your draft is visible before publication.</p>
      </aside>
    </div>
  </main>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useStorage } from '@/stores'
import { pluginWorkspaces } from '@/shell/workspaces'
import { makePluginsApi } from '@/stores/api/plugins'
import { resolveInstallationRuntime } from '@/workspaces/plugins/host/runtime'
import { semver, summary } from '@/workspaces/plugins/version'
import { releaseLabel, installedLabel, draftLabel, hasPublishedUpdate } from './cardVersions.js'
import IconAction from '@/components/_shared/IconAction.vue'
import { filterExtensions, engineName } from './catalog.js'

defineOptions({ name: 'PluginDirectoryWorkspace' })

const categoryTabs = [
  { key: 'all', label: 'All' }, { key: 'games', label: 'Games' }, { key: 'trading', label: 'Trading' }, { key: 'visualizations', label: 'Visualizations' }, { key: 'tools', label: 'Tools' },
]
const ownershipTabs = [
  { key: 'all', label: 'All' }, { key: 'owned', label: 'Owned' }, { key: 'available', label: 'Available' },
]
const store = useStorage()
const api = makePluginsApi()
const deployVersion = String(import.meta.env.VITE_APP_VERSION || '').trim()
const activeCategory = ref('all')
const selectedEngine = ref('')
const engines = ref([])
const ownershipFilter = ref('all')
const filterMenuOpen = ref(false)
const selectedId = ref('')
const busy = ref(false)
const error = ref('')
const catalogExtensions = ref([])
const characterCatalog = ref([])
const characterCatalogReady = ref(false)
const brokenIcons = reactive({})
const showAuthoringHelp = ref(false)
const runningDraftPluginId = ref(readRunningDraftPluginId())

const canCreateExtensions = computed(() => {
  const role = String(store.user?.role || store.state?.user?.role || '').toLowerCase()
  return role === 'admin' || role === 'developer'
})

const installedExtensions = computed(() => pluginWorkspaces(store.uiPluginInstallations || []).map(normalizeInstalled))
const catalogPluginIds = computed(() => new Set(catalogExtensions.value.map(item => item.pluginId)))
const extensions = computed(() => {
  const installed = new Map(installedExtensions.value.map(item => [item.pluginId, item]))
  return [
    ...catalogExtensions.value.map(item => {
      const current = installed.get(item.pluginId)
      if (!current) return item
      // Installation activity changes independently of the catalog. Never retain stale active/manifest state.
      const manifest = item.draftVersion?.id ? item.manifest : current.manifest
      return { ...item, installation: current.installation, active: current.active, enabled: current.enabled, manifest }
    }),
    ...installedExtensions.value.filter(item => !catalogPluginIds.value.has(item.pluginId)),
  ]
})
const filteredExtensions = computed(() => filterExtensions(extensions.value, { engine: selectedEngine.value, category: activeCategory.value, ownership: ownershipFilter.value }))
const ownershipFilterLabel = computed(() => ownershipTabs.find(tab => tab.key === ownershipFilter.value)?.label || 'All')
const activeExtension = computed(() => installedExtensions.value.find(item => item.active) || null)
const selectedExtension = computed(() => extensions.value.find(item => item.id === selectedId.value) || null)

onMounted(() => {
  void refreshCatalog()
  void refreshCharacterCatalog()
  window.addEventListener('aime-draft-preview', onDraftPreview)
  window.addEventListener('aime-draft-preview-clear', onDraftPreviewClear)
  document.addEventListener('pointerdown', onDocumentPointerDown)
})

onBeforeUnmount(() => {
  window.removeEventListener('aime-draft-preview', onDraftPreview)
  window.removeEventListener('aime-draft-preview-clear', onDraftPreviewClear)
  document.removeEventListener('pointerdown', onDocumentPointerDown)
})

async function refreshCatalog() {
  try {
    const [result, catalog] = await Promise.all([api.listPlugins(), api.listEngines()])
    engines.value = Array.isArray(catalog?.items) ? catalog.items : []
    const plugins = Array.isArray(result?.items) ? result.items : []
    const installedByPlugin = new Map(installedExtensions.value.map(item => [item.pluginId, item]))
    catalogExtensions.value = plugins
      .map(plugin => normalizeCatalogSummary(plugin, installedByPlugin.get(String(plugin.id))))
      .sort((a, b) => a.label.localeCompare(b.label))
  } catch (e) {
    error.value = e?.message || String(e)
  }
}

async function refreshCharacterCatalog() {
  try {
    const result = await api.listCharacterCatalog()
    characterCatalog.value = Array.isArray(result?.items) ? result.items : []
  } catch (e) {
    error.value = e?.message || String(e)
    characterCatalog.value = []
  } finally {
    characterCatalogReady.value = true
  }
}

async function refreshAuthorLifecycle(pluginId) {
  const index = catalogExtensions.value.findIndex(item => item.pluginId === String(pluginId))
  if (index < 0) return
  const current = catalogExtensions.value[index]
  if (!current.author || current.lifecycleReady) return
  try {
    const result = await api.listVersions(pluginId)
    const versions = Array.isArray(result?.items) ? result.items : []
    const installed = installedExtensions.value.find(item => item.pluginId === String(pluginId))
    const next = normalizeAuthorLifecycle(current.plugin, versions, installed)
    next.lifecycleReady = true
    catalogExtensions.value = catalogExtensions.value.map((item, i) => i === index ? next : item)
  } catch (e) {
    error.value = e?.message || String(e)
  }
}

function isNextDraft(draft, plugin) {
  if (!draft || draft.status !== 'draft' || !draft.semantic_version) return false
  const current = String(draft.semantic_version).replace(/^v/, '')
  const published = String(plugin?.published_semantic_version || '').replace(/^v/, '')
  return !published || current !== published
}
function normalizeCatalogSummary(plugin, installed) {
  const installation = installed?.installation || null
  const author = plugin?.owned === true
  const candidate = author && plugin?.draft?.status === 'draft' ? plugin.draft : null
  const draft = isNextDraft(candidate, plugin) ? candidate : null
  const runtime = installation ? resolveInstallationRuntime(installation) : resolveInstallationRuntime(draft || plugin) || 'iframe'
  const manifest = draft?.manifest?.characters?.variants?.length
    ? asObject(draft.manifest)
    : asObject(installation?.resolved_manifest || installation?.resolvedManifest || plugin?.published_manifest || plugin?.manifest)
  const hasPublished = String(plugin?.status || '').toLowerCase() === 'published' || Boolean(plugin?.published_semantic_version || plugin?.published_version)
  const state = installation ? 'Installed' : String(plugin?.review_status || '') === 'pending' ? 'Review' : hasPublished ? 'Published' : draft ? 'Draft' : 'Draft'
  return baseExtension({
    id: `extension:${plugin.id}`,
    pluginId: String(plugin.id),
    plugin,
    version: installation ? null : draft,
    draftVersion: draft,
    draftRuntime: draft ? resolveInstallationRuntime(draft) : '',
    installation,
    state,
    active: installed?.active === true,
    enabled: installed?.enabled === true,
    author,
    owned: installation ? String(plugin.access_policy || 'free').toLowerCase() === 'free' || !!installation.entitlement : author,
    runtime,
    history: [],
    latestPublishedVersion: plugin?.published_semantic_version ? { semantic_version: plugin.published_semantic_version, version: plugin.published_version, source_commit_sha: plugin.published_source_commit_sha || plugin.publishedSourceCommitSha || '' } : null,
    entrypoint: String(installation?.entrypoint || draft?.entrypoint || (isLuaRuntime(runtime) ? 'main.lua' : 'index.html')).trim(),
    semanticVersion: String(draft?.semantic_version || installation?.resolved_semantic_version || plugin?.published_semantic_version || '').trim(),
    resolvedVersion: Number(draft?.version || installation?.resolved_version || plugin?.published_version || 0),
    manifest,
    iconUrl: presentationURL(manifest, path => draft ? ownerAssetURL(plugin.id, draft.id, path) : installation ? runtimeAssetURL(installation.id, path) : ''),
  })
}

function normalizeAuthorLifecycle(plugin, versions, installed) {
  const history = [...versions].sort((a, b) => Number(b.version || 0) - Number(a.version || 0))
  const candidate = history.find(version => version?.status === 'draft') || null
  const draft = isNextDraft(candidate, plugin) ? candidate : null
  const published = history.find(version => version?.status === 'published' && Number(version.version) === Number(plugin?.published_version)) || null
  const installation = installed?.installation || null
  const version = installation ? null : (published || draft || history[0] || {})
  const state = installation ? 'Installed' : String(plugin?.review_status || '') === 'pending' ? 'Review' : published ? 'Published' : draft ? 'Draft' : 'Draft'
  const runtime = installation
    ? resolveInstallationRuntime(installation)
    : resolveInstallationRuntime(version) || resolveInstallationRuntime(plugin) || 'iframe'
  const manifest = draft?.manifest?.characters?.variants?.length
    ? asObject(draft.manifest)
    : installation
      ? asObject(installation.resolved_manifest || installation.resolvedManifest)
      : asObject(version?.manifest)
  return baseExtension({
    id: `extension:${plugin.id}`, pluginId: String(plugin.id), plugin, version, installation,
    draftVersion: draft,
    draftRuntime: draft ? resolveInstallationRuntime(draft) : '',
    state, active: installed?.active === true, enabled: installed?.enabled === true, author: true, owned: true, runtime, history,
    latestPublishedVersion: published,
    entrypoint: String(installation?.entrypoint || version?.entrypoint || (isLuaRuntime(runtime) ? 'main.lua' : 'index.html')).trim(),
    semanticVersion: String(draft?.semantic_version || installation?.resolved_semantic_version || version?.semantic_version || '').trim(),
    resolvedVersion: Number(draft?.version || installation?.resolved_version || version?.version || 0),
    manifest,
    iconUrl: presentationURL(manifest, path => draft ? ownerAssetURL(plugin.id, draft.id, path) : installation ? runtimeAssetURL(installation.id, path) : ownerAssetURL(plugin.id, version.id, path)),
  })
}

function normalizeInstalled(workspace) {
  const installation = workspace?.installation || {}
  const plugin = installation.plugin || installation
  const runtime = resolveInstallationRuntime(installation)
  const manifest = asObject(installation.resolved_manifest || installation.resolvedManifest)
  return baseExtension({
    ...workspace, id: `installed:${installation.id}`, pluginId: String(installation.plugin_id || plugin.id || ''), plugin, version: null, installation,
    state: 'Installed', active: installation.active === true, enabled: installation.enabled !== false, author: false,
    owned: String(plugin.access_policy || 'free').toLowerCase() === 'free' || !!installation.entitlement,
    runtime, entrypoint: String(installation.entrypoint || (isLuaRuntime(runtime) ? 'main.lua' : 'index.html')).trim(),
    semanticVersion: String(installation.resolved_semantic_version || plugin.published_semantic_version || '').trim(),
    resolvedVersion: Number(installation.resolved_version || 0), manifest, history: [],
    latestPublishedVersion: installation.resolved_semantic_version ? { semantic_version: installation.resolved_semantic_version, version: installation.resolved_version, source_commit_sha: installation.resolved_source_commit_sha || installation.resolvedSourceCommitSha || '' } : null,
    iconUrl: presentationURL(manifest, path => runtimeAssetURL(installation.id, path)),
  })
}

function baseExtension(input) {
  const plugin = input.plugin || {}
  const kind = String(plugin.kind || input.kind || 'custom').toLowerCase()
  return {
    ...input,
    label: String(input.label || plugin.name || plugin.slug || 'Extension').trim() || 'Extension',
    description: String(plugin.description || '').trim(), kind,
    engine: String(plugin.engine || input.engine || '').trim(),
    category: normalizeCategory(plugin.category || input.category, kind), tags: normalizeTags(plugin.tags || input.tags),
    status: input.state === 'Draft' ? 'draft' : String(plugin.status || input.state || 'unknown').toLowerCase(),
    sdkVersion: String(input.version?.sdk_version || input.installation?.sdk_version || plugin.sdk_version || 'aime.ui-plugin.sdk.v1'),
    accessPolicy: String(plugin.access_policy || 'free').toLowerCase(), priceLamports: Number(plugin.price_lamports || 0),
    authorName: String(plugin.author || 'admin').trim().replace(/^@+/, '') || 'admin',
  }
}

function asObject(value) { return value && typeof value === 'object' && !Array.isArray(value) ? value : {} }
function presentationPath(manifest) {
  const presentation = asObject(manifest.presentation)
  const path = String(presentation.icon || presentation.cover || '').trim()
  if (!path || path.startsWith('/') || path.includes('://') || path.includes('\\') || path.split('/').some(part => part === '..' || part === '.')) return ''
  return path
}
function presentationURL(manifest, resolve) { const path = presentationPath(manifest); return path ? resolve(path) : '' }
function characterItems(extension) {
  if (!characterCatalogReady.value) return []
  const pluginId = String(extension?.pluginId || '').trim()
  const unique = new Map()
  for (const item of characterCatalog.value) {
    if (String(item?.plugin_id || '').trim() !== pluginId) continue
    const id = String(item?.character?.id || '').trim()
    if (!id) continue
    const current = unique.get(id)
    if (!current || (!current.owned && item?.owned)) unique.set(id, item)
  }
  return [...unique.values()]
}
function characterVariants(extension) {
  return characterItems(extension)
    .filter(item => item?.character?.art?.portrait)
    .slice(0, 6)
    .map(item => ({ ...item.character, catalogVersionId: String(item.version_id || '') }))
}
function characterOverflow(extension) {
  return Math.max(0, characterItems(extension).length - characterVariants(extension).length)
}
function characterAssetURL(extension, variant) {
  const pluginId = String(extension?.pluginId || '').trim()
  const versionId = String(variant?.catalogVersionId || '').trim()
  const path = String(variant?.art?.portrait || '').replace(/^\/+/, '')
  if (!pluginId || !versionId || !path) return ''
  return `/api/plugins/characters/media/${encodeURIComponent(pluginId)}/${encodeURIComponent(versionId)}/${encodedAssetPath(path)}`
}
function characterImageStyle(variant) {
  const frame = variant?.art?.frame
  const sheet = variant?.art?.sheet
  const width = Number(frame?.width)
  const height = Number(frame?.height)
  const sheetWidth = Number(sheet?.width)
  const sheetHeight = Number(sheet?.height)
  const x = Number(frame?.x) || 0
  const y = Number(frame?.y) || 0
  if (!(width > 0) || !(height > 0)) return {}
  const scale = Math.max(30 / width, 38 / height)
  if (sheetWidth > 0 && sheetHeight > 0) {
    return {
      width: `${sheetWidth * scale}px`,
      height: `${sheetHeight * scale}px`,
      maxWidth: 'none',
      transform: `translate(${-x * scale}px, ${-y * scale}px)`,
    }
  }
  return { width: '100%', height: '100%', objectFit: 'cover' }
}
function encodedAssetPath(path) { return path.split('/').map(encodeURIComponent).join('/') }
function ownerAssetURL(pluginId, versionId, path) { return `/api/plugins/authoring/plugins/${encodeURIComponent(pluginId)}/versions/${encodeURIComponent(versionId)}/preview/${encodedAssetPath(path)}` }
function runtimeAssetURL(installationId, path) { return `/api/plugins/runtime/${encodeURIComponent(installationId)}/${encodedAssetPath(path)}` }

function normalizeCategory(raw, kind = '') {
  const value = String(raw || '').toLowerCase()
  if (categoryTabs.some(tab => tab.key === value && value !== 'all')) return value
  if (kind === 'game') return 'games'
  if (kind === 'terminal') return 'trading'
  if (kind === 'visualization') return 'visualizations'
  return 'tools'
}
function normalizeTags(raw) { const rows = Array.isArray(raw) ? raw : String(raw || '').split(','); return [...new Set(rows.map(tag => String(tag || '').trim().toLowerCase()).filter(Boolean))] }
function selectExtension(extension) {
  filterMenuOpen.value = false
  selectedId.value = extension.id
  error.value = ''
  if (extension?.author) void refreshAuthorLifecycle(extension.pluginId)
}
function closeDetail() { if (busy.value) return; selectedId.value = ''; error.value = ''; filterMenuOpen.value = false }
function openAuthoringHelp() { filterMenuOpen.value = false; showAuthoringHelp.value = true }
function onDocumentPointerDown(event) {
  if (!filterMenuOpen.value) return
  const target = event?.target
  if (target instanceof Element && target.closest('.filter-picker')) return
  filterMenuOpen.value = false
}
function openCharacters(extension) {
  const pluginId = String(extension?.pluginId || '').trim()
  if (!pluginId) return
  closeDetail()
  window.dispatchEvent(new CustomEvent('aime-shell-workspace', {
    detail: { workspace: 'nft', filter: { pluginId } },
  }))
}
function updateAvailable(extension) {
  return hasPublishedUpdate(extension)
}
async function updateInstalled(extension) {
  if (busy.value || !updateAvailable(extension)) return
  busy.value = true; error.value = ''
  try {
    const result = await api.listVersions(extension.pluginId)
    const versions = Array.isArray(result?.items) ? result.items : []
    const published = versions.find(item => item.status === 'published' && Number(item.version) === Number(extension.plugin?.published_version))
    if (!published?.id || !hasPublishedUpdate({ ...extension, latestPublishedVersion: published })) throw new Error('published_update_unavailable')
    await api.updateInstallation(extension.installation.id, published.id)
    await refreshInstallations()
    await refreshCatalog()
    closeDetail()
  } catch (e) { error.value = e?.message || String(e) } finally { busy.value = false }
}
function releaseVersionLabel(extension) {
  const fallback = {
    semantic_version: extension?.plugin?.published_semantic_version || extension?.semanticVersion,
    version: extension?.plugin?.published_version || extension?.resolvedVersion,
    source_commit_sha: extension?.plugin?.published_source_commit_sha || extension?.installation?.resolved_source_commit_sha || '',
  }
  return summary(extension?.latestPublishedVersion || fallback, fallback.semantic_version, fallback.version)
}
function authoringVersionLabel(extension) {
  return summary(extension?.draftVersion) || 'draft'
}
function detailVersionLabel(extension) {
  const release = semver(extension?.latestPublishedVersion?.semantic_version || extension?.plugin?.published_semantic_version)
  if (release) return `Release ${release}`
  const draft = semver(extension?.draftVersion?.semantic_version)
  return draft ? `Draft ${draft}` : 'Draft'
}
function stateLabel(extension) {
  if (extension?.installation) return 'Installed'
  if (extension?.state === 'Draft') return 'Draft'
  if (extension?.state === 'Review') return 'In review'
  if (extension?.state === 'Published') return 'Published'
  return extension?.state || 'Unknown'
}
function fallbackIcon(extension) { const kind = String(extension.kind || 'custom'); return kind === 'game' ? 'G' : kind === 'terminal' ? 'T' : kind === 'visualization' ? 'V' : 'X' }
function kindLabel(extension) { const kind = String(extension.kind || 'custom'); return kind.charAt(0).toUpperCase() + kind.slice(1) }
function categoryLabel(category) { return categoryTabs.find(tab => tab.key === category)?.label || 'Tools' }
function engineLabel(id) { return engineName(id, engines.value) }

async function updateEngine(extension, engine) {
  if (busy.value || !extension.author || engine === extension.engine) return
  busy.value = true
  error.value = ''
  try {
    const plugin = extension.plugin
    await api.updatePlugin(extension.pluginId, {
      slug: plugin.slug, name: plugin.name, description: plugin.description || '',
      kind: plugin.kind, category: plugin.category, tags: plugin.tags || [],
      runtime: plugin.runtime, sdk_version: plugin.sdk_version, engine,
    })
    await refreshInstallations()
    await refreshCatalog()
  } catch (e) { error.value = e?.message || String(e) } finally { busy.value = false }
}
function isLuaRuntime(runtime) { return ['lua', 'game', 'game-lua'].includes(String(runtime || '').trim().toLowerCase()) }
function runtimeLabel(runtime) { return isLuaRuntime(runtime) ? 'Lua' : String(runtime || 'iframe').trim().toLowerCase() === 'iframe' ? 'Iframe' : String(runtime || 'Unknown') }

function readRunningDraftPluginId() {
  try {
    const value = JSON.parse(window.localStorage?.getItem('aime.workspace.ownerDraft') || 'null')
    return String(value?.pluginId || '').trim()
  } catch { return '' }
}
function onDraftPreview(event) { runningDraftPluginId.value = String(event?.detail?.pluginId || '').trim() }
function onDraftPreviewClear() { runningDraftPluginId.value = '' }
function isDraftRunning(extension) { return runningDraftPluginId.value === String(extension?.pluginId || '') }
function isRunning(extension) { return isDraftRunning(extension) || extension?.active === true }
function canPreviewDraft(extension) {
  return extension?.author && extension?.draftVersion && isLuaRuntime(extension.draftRuntime || extension.runtime)
}
function primaryActionIcon(extension) {
  if (updateAvailable(extension)) return isRunning(extension) ? 'reset' : 'download'
  if (isRunning(extension)) return 'pause'
  if (!extension?.installation && !canPreviewDraft(extension)) return 'download'
  return 'play'
}
function primaryActionLabel(extension) {
  if (updateAvailable(extension)) return 'Update to latest release'
  if (isRunning(extension)) return 'Pause Plugin'
  if (!extension?.installation && !canPreviewDraft(extension)) return 'Install Plugin'
  return canPreviewDraft(extension) ? 'Play draft' : 'Play Plugin'
}
function primaryActionText(extension) {
  if (updateAvailable(extension)) return 'Update'
  if (isRunning(extension)) return 'Disable'
  if (!extension?.installation && !canPreviewDraft(extension)) return 'Install'
  return 'Enable'
}
function runPrimaryAction(extension) {
  if (updateAvailable(extension)) { void updateInstalled(extension); return }
  if (isRunning(extension)) { void stopExtension(extension); return }
  if (!extension?.installation && !canPreviewDraft(extension)) { void installPublished(extension); return }
  if (canPreviewDraft(extension)) { void previewDraft(extension); return }
  void toggleActive(extension)
}

async function previewDraft(extension) {
  const draft = extension?.draftVersion || (extension?.state === 'Draft' ? extension.version : null)
  const runtime = extension?.draftRuntime || resolveInstallationRuntime(draft) || extension?.runtime
  if (!draft?.id || !extension.pluginId || !isLuaRuntime(runtime)) return
  if (busy.value) return
  busy.value = true; error.value = ''
  try {
    const active = installedExtensions.value.find(item => item.active)
    if (active?.installation?.id) {
      await api.deactivateInstallation(active.installation.id)
      await refreshInstallations()
    }
    const detail = { pluginId: extension.pluginId, versionId: draft.id, version: Number(draft.version || 0), name: extension.label, semanticVersion: String(draft.semantic_version || 'draft'), sdkVersion: extension.sdkVersion }
    closeDetail()
    window.dispatchEvent(new CustomEvent('aime-shell-workspace', { detail: { workspace: 'console' } }))
    window.dispatchEvent(new CustomEvent('aime-shell-section', { detail: { section: 'home' } }))
    window.setTimeout(() => window.dispatchEvent(new CustomEvent('aime-draft-preview', { detail })), 0)
  } catch (e) { error.value = e?.message || String(e) } finally { busy.value = false }
}

async function installPublished(extension) {
  if (busy.value || extension?.installation) return
  busy.value = true; error.value = ''
  try {
    await api.installPlugin(extension.pluginId, extension.version?.id || '')
    await refreshInstallations()
    await refreshCatalog()
    closeDetail()
  } catch (e) { error.value = e?.message || String(e) } finally { busy.value = false }
}

async function stopExtension(extension) {
  if (busy.value) return
  if (isDraftRunning(extension)) {
    window.dispatchEvent(new CustomEvent('aime-draft-preview-clear'))
    const active = installedExtensions.value.find(item => item.active)
    const workspace = active ? `plugin:${active.installation.id}` : 'console'
    await nextTick()
    window.dispatchEvent(new CustomEvent('aime-shell-workspace', { detail: { workspace } }))
    window.dispatchEvent(new CustomEvent('aime-shell-section', { detail: { section: 'home' } }))
    return
  }
  if (extension?.active) await toggleActive(extension)
}

async function removeInstallation(extension) {
  const installationId = String(extension?.installation?.id || '').trim()
  if (!installationId || busy.value) return
  if (!window.confirm(`Remove “${extension.label}” from installed Extensions?`)) return
  busy.value = true; error.value = ''
  try {
    await api.uninstallInstallation(installationId)
    if (isDraftRunning(extension)) window.dispatchEvent(new CustomEvent('aime-draft-preview-clear'))
    await refreshInstallations()
    await refreshCatalog()
    await nextTick()
    window.dispatchEvent(new CustomEvent('aime-shell-workspace', { detail: { workspace: 'console' } }))
    window.dispatchEvent(new CustomEvent('aime-shell-section', { detail: { section: 'home' } }))
    closeDetail()
  } catch (e) { error.value = e?.message || String(e) } finally { busy.value = false }
}

async function removeDraft(extension) {
  const draft = extension?.draftVersion || (extension?.state === 'Draft' ? extension.version : null)
  if (busy.value || !draft?.id) return
  if (!window.confirm(`Delete mutable draft ${draft.semantic_version || 'draft'}? Published history will remain immutable.`)) return
  busy.value = true; error.value = ''
  try {
    await api.deleteDraft(extension.pluginId, draft.id)
    window.dispatchEvent(new CustomEvent('aime-draft-preview-clear'))
    await refreshCatalog()
    closeDetail()
  } catch (e) { error.value = e?.message || String(e) } finally { busy.value = false }
}

async function removeExtension(extension) {
  if (busy.value || extension?.history?.some(item => item.status === 'published')) return
  if (!window.confirm(`Delete draft-only Extension “${extension.label}” completely?`)) return
  busy.value = true; error.value = ''
  try {
    await api.deleteDraftOnlyPlugin(extension.pluginId)
    window.dispatchEvent(new CustomEvent('aime-draft-preview-clear'))
    await refreshCatalog()
    closeDetail()
  } catch (e) { error.value = e?.message || String(e) } finally { busy.value = false }
}

async function reopenAsDraft(extension) {
  const source = extension?.history?.find(item => item.status === 'published')
  if (busy.value || !source?.id) return
  if (!window.confirm(`Reopen ${source.semantic_version || 'published version'} as the current draft? The active publication will be removed.`)) return
  busy.value = true; error.value = ''
  try {
    await api.reopenPublishedAsDraft(extension.pluginId, source.id)
    await refreshInstallations()
    await refreshCatalog()
    closeDetail()
  } catch (e) { error.value = e?.message || String(e) } finally { busy.value = false }
}

async function toggleActive(extension) {
  const installationId = String(extension?.installation?.id || '').trim()
  if (!installationId || busy.value) return
  busy.value = true; error.value = ''
  try {
    if (extension.active) await api.deactivateInstallation(installationId)
    else await api.activateInstallation(installationId)
    const items = await refreshInstallations()
    const fresh = items.find(item => String(item?.id || '') === installationId)
    await nextTick()
    if (fresh?.active) {
      window.dispatchEvent(new CustomEvent('aime-draft-preview-clear'))
      await nextTick()
      window.dispatchEvent(new CustomEvent('aime-shell-workspace', { detail: { workspace: `plugin:${installationId}` } }))
      window.dispatchEvent(new CustomEvent('aime-shell-section', { detail: { section: 'home' } }))
    }
    await refreshCatalog()
    closeDetail()
  } catch (e) { error.value = e?.message || String(e) } finally { busy.value = false }
}

async function refreshInstallations() {
  const result = await api.listInstallations()
  const items = Array.isArray(result?.items) ? result.items : []
  if (typeof store.setUIPluginInstallations === 'function') store.setUIPluginInstallations(items)
  else if (store.state?.value) store.state.value.uiPluginInstallations = items
  else if (store.state) store.state.uiPluginInstallations = items
  return items
}
function accessLabel(extension) { if (extension.state === 'Draft') return 'Owner preview'; if (extension.accessPolicy === 'nft') return extension.owned ? 'NFT owned' : 'NFT required'; if (extension.priceLamports > 0) return `${(extension.priceLamports / 1_000_000_000).toLocaleString(undefined, { maximumFractionDigits: 4 })} SOL`; return 'Free' }
function selectOwnershipFilter(key) { ownershipFilter.value = ownershipTabs.some(tab => tab.key === key) ? key : 'all'; filterMenuOpen.value = false }
</script>

<style scoped>
.engine-filter{display:flex;align-items:center;gap:8px;padding:7px 10px;color:#94a3b8;font-size:11px;border-bottom:1px solid #202020}.engine-filter select,.facts select{max-width:100%;padding:4px 7px;border:1px solid #334155;border-radius:6px;background:#101214;color:#dbeafe;font:inherit}.engine-badge{font-weight:800;border:1px solid #334155;border-radius:5px;padding:1px 5px;white-space:nowrap}

.extensions-directory{position:relative;width:100%;height:100%;min-height:0;padding:8px 8px calc(var(--app-bottom-nav-space,58px) + env(safe-area-inset-bottom));background:#0a0a0a;overflow:hidden}.panel{height:100%;min-height:0;display:flex;flex-direction:column;border:1px solid #222;border-radius:8px;background:#111;overflow:hidden}.header{min-height:50px;padding:0 12px;display:flex;align-items:center;justify-content:space-between;gap:8px;border-bottom:1px solid #222;background:#1a1a1a}.title-line{display:flex;align-items:baseline;gap:8px}.title-line>span{color:#fff;font-size:14px;font-weight:700}.title-line small,.active-hint{color:#8fa1b5;font-size:11px}.tabs{display:flex;gap:6px;min-height:42px;align-items:center;padding:6px 10px;border-bottom:1px solid #202020;overflow-x:auto}.tabs button{height:28px;padding:0 10px;border:1px solid #2b3139;border-radius:8px;background:#101214;color:#aab4c0;font-size:12px;font-weight:700;white-space:nowrap}.tabs button.active{border-color:#2563eb;color:#dbeafe;background:#10213a}.body{flex:1;min-height:0;padding:8px;overflow-y:auto}.extension-grid{list-style:none;padding:0;margin:0;display:grid;grid-template-columns:repeat(auto-fill,minmax(270px,1fr));gap:8px}.extension-card{display:grid;grid-template-columns:58px minmax(0,1fr);gap:11px;min-width:0;padding:10px;border:1px solid #222;border-radius:10px;background:#0f0f0f;color:#e5e7eb;cursor:pointer;overflow:hidden}.extension-card:hover{border-color:#415166;background:#13171d}.extension-card.active{border-color:#2563eb;box-shadow:inset 0 0 0 1px #2563eb44}.extension-media,.detail-icon{overflow:hidden;background:#17202b;display:grid;place-items:center;color:#7dd3fc;font-weight:900}.extension-media{width:58px;height:58px;border-radius:12px}.extension-media img,.detail-icon img{width:100%;height:100%;object-fit:cover;display:block}.extension-main{position:relative;min-width:0;display:grid;align-content:start;gap:5px}.extension-head{min-width:0;display:flex;align-items:baseline;gap:7px}.extension-head strong{color:#fff;font-size:14px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;min-width:0}.extension-head>span{margin-left:auto;color:#93c5fd;font-size:10px;text-align:right;overflow-wrap:anywhere;min-width:0;flex-shrink:1}.extension-main p{margin:0;color:#7f8b9b;font-size:11px;line-height:1.35;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}.extension-meta,.tags,.detail-tags,.detail-footer{display:flex;gap:7px;align-items:center;flex-wrap:wrap}.card-footer{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:6px;align-items:center;min-width:0}.extension-meta{color:#8390a3;font-size:10px}.extension-meta .state{font-weight:800}.extension-meta .draft,.status-pill.draft{color:#fcd34d}.extension-meta .published,.status-pill.published{color:#c4b5fd}.extension-meta .installed,.status-pill.installed{color:#86efac}.extension-meta .authoring{color:#fcd34d;font-weight:800}.extension-meta>span{overflow-wrap:anywhere}.tags span,.detail-tags span{color:#93c5fd;font-size:10px}.detail-footer{justify-content:space-between}.card-actions{display:flex;align-items:center;gap:4px;margin:0}.card-status{min-width:0;display:grid;grid-template-columns:minmax(0,1fr) auto;grid-template-rows:auto auto;align-items:center;gap:2px 7px;padding-right:0}.card-status .extension-meta{grid-column:1;grid-row:1}.card-status .developer{grid-column:1;grid-row:2}.card-primary-action{grid-column:2;grid-row:1/3;align-self:stretch;min-width:64px}.card-footer>.tags{min-width:0;overflow:hidden;flex-wrap:nowrap}.rating{margin:0;color:#64748b;font-size:13px;letter-spacing:1px;white-space:nowrap}.empty{grid-column:1/-1;min-height:150px;display:grid;place-items:center;border:1px dashed #263039;border-radius:8px;text-align:center;color:#cbd5e1}.detail-overlay{position:absolute;inset:0;z-index:20;display:grid;place-items:center;padding:18px;background:#000a;backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px)}.detail-card{position:relative;width:min(520px,100%);max-height:calc(100% - 8px);overflow:auto;display:grid;gap:14px;padding:16px;border:1px solid #26313c;border-radius:12px;background:#0b0f12;color:#dbe5ef;box-shadow:0 20px 80px #000c}.detail-close{position:absolute;right:10px;top:8px;width:30px;height:30px;border:0;background:transparent;color:#94a3b8;font-size:24px;cursor:pointer}.detail-head{display:flex;justify-content:space-between;gap:12px;align-items:flex-start;padding-right:28px}.detail-title{display:flex;gap:10px;min-width:0}.detail-icon{width:48px;height:48px;border-radius:10px;flex:0 0 48px}.detail-head small{color:#7dd3fc;text-transform:uppercase;font-size:9px;font-weight:800}.detail-head h2{margin:2px 0 0;color:#fff;font-size:18px}.detail-version{display:flex;align-items:baseline;gap:7px;margin-top:2px}.detail-version strong{color:#93c5fd;font-size:10px;font-weight:700}.detail-version span{color:#697586;font-size:9px;font-style:italic}.status-pill{padding:4px 8px;border:1px solid #374151;border-radius:999px;color:#94a3b8;font-size:10px;font-weight:800}.status-pill.draft{border-color:#92400e;background:#271505}.status-pill.published{border-color:#5b21b6;background:#1e1039}.status-pill.installed{border-color:#166534;background:#07170d}.description{margin:0;color:#94a3b8;font-size:12px;line-height:1.5}.facts{display:grid;gap:0;margin:0;border:1px solid #1f2933;border-radius:8px;overflow:hidden}.facts>div{display:grid;grid-template-columns:92px minmax(0,1fr);gap:10px;padding:8px 10px;border-bottom:1px solid #1f2933}.facts>div:last-child{border-bottom:0}.facts dt{color:#718096;font-size:11px}.facts dd{margin:0;color:#dbe5ef;font-size:11px;overflow-wrap:anywhere}.mono{font-family:ui-monospace,SFMono-Regular,Menlo,monospace}.detail-actions{display:flex;justify-content:flex-end;gap:6px;flex-wrap:nowrap;overflow-x:auto}.icon-action{width:38px;height:38px;display:grid;place-items:center;padding:0;border:1px solid #2563eb;border-radius:8px;background:#10213a;color:#dbeafe;font-size:18px;font-weight:900;cursor:pointer}.icon-action.danger{border-color:#6b3434;background:#241111;color:#fecaca}.icon-action:disabled{opacity:.45;cursor:default}.error{padding:8px 10px;border-radius:7px;background:#251010;color:#fca5a5;font-size:12px}@media(max-width:760px){.active-hint{display:none}.extension-grid{grid-template-columns:1fr}.detail-overlay{padding:8px}.detail-card{max-height:100%}}
.character-strip{display:flex;align-items:center;gap:6px;margin:8px 0 2px}.character-portrait{position:relative;width:30px;height:38px;display:block;overflow:hidden;border:1px solid #ffffff26;border-radius:7px;background:#0b1115}.character-portrait img{position:absolute;left:0;top:0;transform-origin:top left}.character-strip>span:last-child{margin-left:2px;color:#8fa1b5;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.06em}
.header-actions{display:flex;align-items:center;gap:9px}.authoring-help{width:32px;height:32px;display:grid;place-items:center;border:1px solid #7c3aed;border-radius:9px;background:linear-gradient(135deg,#312e81,#7c3aed 58%,#db2777);color:#fff;font-size:17px;box-shadow:0 0 18px #7c3aed55;cursor:pointer}.developer{color:#64748b;font-size:9px}.authoring-card{width:min(440px,100%);grid-template-columns:46px 1fr}.wand-mark{width:42px;height:42px;display:grid;place-items:center;border-radius:12px;background:linear-gradient(135deg,#312e81,#7c3aed,#db2777);font-size:23px}.authoring-card h2{margin:2px 0 0;color:#fff;font-size:20px}.eyebrow{color:#c4b5fd;font-size:9px;font-weight:900;letter-spacing:.12em}.authoring-card>.description,.wizard-steps,.authoring-note{grid-column:1/-1}.wizard-steps{display:grid;gap:8px;margin:0;padding:0;list-style:none;counter-reset:steps}.wizard-steps li{counter-increment:steps;display:grid;grid-template-columns:24px 64px 1fr;gap:8px;align-items:start}.wizard-steps li:before{content:counter(steps);width:22px;height:22px;display:grid;place-items:center;border-radius:50%;background:#172554;color:#bfdbfe;font-size:10px;font-weight:900}.wizard-steps strong{font-size:11px;color:#e5e7eb}.wizard-steps span{font-size:10px;line-height:1.45;color:#94a3b8}.authoring-note{margin:0;padding:9px 10px;border:1px solid #342b58;border-radius:8px;background:#171127;color:#c4b5fd;font-size:10px;line-height:1.45}
.filter-picker{position:relative}.filter-menu{position:absolute;z-index:24;right:0;top:36px;min-width:126px;padding:5px;border:1px solid #2b3540;border-radius:9px;background:#090d12f5;box-shadow:0 14px 40px #000c;backdrop-filter:blur(12px)}.filter-menu button{width:100%;min-height:34px;padding:7px 9px;border:0;border-radius:6px;background:transparent;color:#aab4c0;text-align:left;font-size:11px;font-weight:800;cursor:pointer}.filter-menu button.active{background:#10213a;color:#dbeafe}
</style>
